import { useCallback } from 'react';

import type { GlobalLinks } from 'models/sitecore';
import type { URLString } from 'types';
import { ignorePromiseRejection, is } from 'utils/helpers';
import { createUrl, type URLParams } from 'utils/url';

import { useGlobalLinks } from '../useSitecoreContext';

import { usePath } from './usePath';
import { useRouter } from './useRouter';
import type { ParamValue } from './useSearchParams';

export function useNavigation() {
	const router = useRouter();
	const { parentPath } = usePath();
	const links = useGlobalLinks();

	const navigate = useCallback(
		(
			{
				location,
				query,
			}: {
				location: keyof GlobalLinks | URLString;
				query?: Partial<URLParams<keyof ParamValue>>;
			},
			options: { replace?: boolean } = {},
		) => {
			const path = is.keyOf(links, location) ? links[location] : location;
			// TODO: replace with assert object assert after merging company site improvements.
			if (!is.string(path)) {
				throw new Error('navigate path missing');
			}
			const url = createUrl(path, query ?? {});
			if (options.replace) {
				ignorePromiseRejection(router?.replace(url));
			} else {
				ignorePromiseRejection(router?.push(url));
			}
		},
		[links, router],
	);

	const navigateToParentPath = useCallback(() => {
		navigate({ location: parentPath as URLString });
	}, [navigate, parentPath]);

	return { navigate, navigateToParentPath };
}
